import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SimpleDialog, { SimpleDialogProps } from '../SimpleDialog/SimpleDialog';
import { InputText } from '../../ui';
import { AccessToken } from '../../AccessTokenManager/AccessTokenManager';
import Success from '../../Success/Success';
import TokenDisplay from '../../TokenDisplay/TokenDisplay';
import Loading from '../../Loading/Loading';
import { createAPITokenCall } from '../../../store/auth/sagas';

interface Props extends Omit<SimpleDialogProps, 'handleOk'> {
  handleOk: (token: AccessToken) => void;
}

const AddAccessTokenDialog: React.FC<Props> = ({ handleOk, ...rest }) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<AccessToken>({
    id: '',
    name: '',
    value: '',
    created_at: '',
    expires_at: '',
    last_used_at: '',
  });

  const createAPIToken = async () => {
    setIsLoading(true);
    // API call
    const result = await createAPITokenCall(data);
    switch (result.type) {
      case 'ok':
        const newToken = result.value.data;
        setData(newToken);
        handleOk(newToken);
        break;
      case 'validation-error':
      case 'error':
        break;
    }
    setIsLoading(false);
  };

  const isCreated = !!data.id;

  return (
    <SimpleDialog
      {...rest}
      title={t('Add token')}
      closeOnOk={false}
      handleOk={isCreated ? undefined : createAPIToken}
      disableOkButton={!data.name}
      cancelButtonText={isCreated ? t('Close') : t('Cancel')}
    >
      {isLoading ? (
        <Loading compact />
      ) : isCreated ? (
        <div className="flex flex-col items-center">
          <Success title={t('Access token created')} message="" />
          <TokenDisplay token={data.value} />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-y-5">
          <InputText
            placeholder={t('Enter token name')}
            label={t('Name')}
            className="text-sm w-72"
            value={data.name}
            onChange={(event) => setData({ ...data, name: event.target.value })}
          />
        </div>
      )}
    </SimpleDialog>
  );
};

export default AddAccessTokenDialog;
